// src/propertyManagement/pages/AddWorkOrderPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  IconButton,
  Checkbox,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import NoteIcon from '@mui/icons-material/Note';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';

export default function AddWorkOrderPage() {
  const navigate = useNavigate();

  // form state
  const [taskMode, setTaskMode]       = useState('new');
  const [taskType, setTaskType]       = useState('');
  const [category, setCategory]       = useState('');
  const [assignedTo, setAssignedTo]   = useState('Jackson Rossi');
  const [collaborators, setCollaborators] = useState([]);
  const [subject, setSubject]         = useState('');
  const [vendor, setVendor]           = useState('');
  const [entryDetails, setEntryDetails] = useState('');
  const [entryContact, setEntryContact] = useState('');
  const [workPerformed, setWorkPerformed] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');
  const [status, setStatus]           = useState('New');
  const [priority, setPriority]       = useState('Normal');
  const [dueDate, setDueDate]         = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [chargeTo, setChargeTo]       = useState('');
  const [lineItems, setLineItems]     = useState([{ qty:1, account:'', description:'', price:0 }]);

  const handleAddRow = () => {
    setLineItems([...lineItems, { qty:1, account:'', description:'', price:0 }]);
  };
  const handleRemoveRow = idx => {
    setLineItems(lineItems.filter((_,i) => i!==idx));
  };

  return (
    <Box sx={{ p:3, display:'flex', flexDirection:'column', gap:3, bgcolor:'#f9fafb' }}>
      {/* Back + Title */}
      <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ textTransform:'none' }}
        >
          Back
        </Button>
        <Typography variant="h4">Add Work Order</Typography>
      </Box>

      {/* Task Details */}
      <Paper elevation={1} sx={{ p:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
          <AssignmentIcon color="primary" />
          <Typography variant="h6">Task Details</Typography>
        </Box>
        <Tabs
          value={taskMode}
          onChange={(_,v)=>setTaskMode(v)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb:3 }}
        >
          <Tab label="Create new task" value="new" />
          <Tab label="Add to existing task" value="existing" />
        </Tabs>
        <Box
          sx={{
            display:'grid',
            gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' },
            gap:2
          }}
        >
          {[
            { label:'Task type',      value:taskType,    setter:setTaskType,    placeholder:'Select task type' },
            { label:'Category',       value:category,    setter:setCategory,    placeholder:'Select category' },
            { label:'Assigned to',    value:assignedTo,  setter:setAssignedTo,  placeholder:'Jackson Rossi' },
            { label:'Collaborators',  value:collaborators, setter:setCollaborators, placeholder:'Select collaborators', multiple: true }
          ].map((fld, i) => (
            <FormControl key={i} size="small" fullWidth>
              <InputLabel>{fld.label}</InputLabel>
              <Select
                multiple={fld.multiple}
                value={fld.value}
                label={fld.label}
                onChange={e => fld.setter(e.target.value)}
                renderValue={val => Array.isArray(val) ? val.join(', ') : val}
              >
                <MenuItem value="">
                  <em>{fld.placeholder}</em>
                </MenuItem>
                {['Option A','Option B','Option C'].map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {fld.multiple && <Checkbox checked={fld.value.includes(opt)} />}
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>
      </Paper>

      {/* Work Order Details */}
      <Paper elevation={1} sx={{ p:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
          <BuildIcon color="primary" />
          <Typography variant="h6">Work Order Details</Typography>
        </Box>
        <Box
          sx={{
            display:'grid',
            gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' },
            gap:2,
            mb:3
          }}
        >
          <TextField
            fullWidth size="small"
            label="Subject"
            value={subject}
            onChange={e=>setSubject(e.target.value)}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Vendor</InputLabel>
            <Select
              value={vendor}
              label="Vendor"
              onChange={e=>setVendor(e.target.value)}
            >
              <MenuItem value=""><em>Select or add new…</em></MenuItem>
              <MenuItem value="Vendor A">Vendor A</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Entry details</InputLabel>
            <Select
              value={entryDetails}
              label="Entry details"
              onChange={e=>setEntryDetails(e.target.value)}
            >
              <MenuItem value=""><em>Select entry preference</em></MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Entry contact</InputLabel>
            <Select
              value={entryContact}
              label="Entry contact"
              onChange={e=>setEntryContact(e.target.value)}
            >
              <MenuItem value=""><em>Select entry contacts…</em></MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField
          fullWidth size="small" label="Work to be performed"
          multiline rows={4}
          value={workPerformed}
          onChange={e=>setWorkPerformed(e.target.value)}
          sx={{ mb:2 }}
        />
        <TextField
          fullWidth size="small" label="Vendor notes"
          multiline rows={3}
          value={vendorNotes}
          onChange={e=>setVendorNotes(e.target.value)}
          sx={{ mb:3 }}
        />
        <Box
          sx={{
            display:'grid',
            gridTemplateColumns:{ xs:'1fr', sm:'1fr 1fr 1fr' },
            gap:2
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={e=>setStatus(e.target.value)}
            >
              {['New','In progress','Completed'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              label="Priority"
              onChange={e=>setPriority(e.target.value)}
            >
              {['Normal','High','Low'].map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small" fullWidth label="Due date" type="date"
            InputLabelProps={{ shrink:true }}
            value={dueDate} onChange={e=>setDueDate(e.target.value)}
          />
        </Box>
      </Paper>

      {/* Files */}
      <Paper elevation={1} sx={{ p:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
          <NoteIcon color="primary" />
          <Typography variant="h6">Files</Typography>
        </Box>
        <Button startIcon={<AddIcon />} size="small">
          Add files
        </Button>
      </Paper>

      {/* Invoice & Line Items */}
      <Paper elevation={1} sx={{ p:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
          <Typography variant="h6">Invoice & Parts/Labor</Typography>
        </Box>
        <Box
          sx={{
            display:'grid',
            gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' },
            gap:2,
            mb:2
          }}
        >
          <TextField
            fullWidth size="small" label="Invoice number"
            value={invoiceNumber} onChange={e=>setInvoiceNumber(e.target.value)}
          />
          <TextField
            fullWidth size="small" label="Charge work to"
            value={chargeTo} onChange={e=>setChargeTo(e.target.value)}
          />
        </Box>
        <Box>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                {['QTY','Account','Description','Price','Total',''].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((row, idx) => {
                const total = row.qty * row.price;
                return (
                  <tr key={idx}>
                    <td style={tdStyle}>
                      <TextField
                        size="small" type="number" value={row.qty}
                        onChange={e => {
                          const newRows = [...lineItems];
                          newRows[idx].qty = +e.target.value;
                          setLineItems(newRows);
                        }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <TextField
                        size="small" value={row.account}
                        onChange={e => {
                          const newRows = [...lineItems];
                          newRows[idx].account = e.target.value;
                          setLineItems(newRows);
                        }}
                        placeholder="Type or select an account"
                        fullWidth
                      />
                    </td>
                    <td style={tdStyle}>
                      <TextField
                        size="small" value={row.description}
                        onChange={e => {
                          const newRows = [...lineItems];
                          newRows[idx].description = e.target.value;
                          setLineItems(newRows);
                        }}
                        fullWidth
                      />
                    </td>
                    <td style={tdStyle}>
                      <TextField
                        size="small" type="number" value={row.price}
                        onChange={e => {
                          const newRows = [...lineItems];
                          newRows[idx].price = +e.target.value;
                          setLineItems(newRows);
                        }}
                      />
                    </td>
                    <td style={tdStyle}>${total.toFixed(2)}</td>
                    <td style={tdStyle}>
                      <IconButton size="small" onClick={() => handleRemoveRow(idx)}>
                        <ClearIcon />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr>
                <td colSpan={4} style={tdStyle}><strong>Total</strong></td>
                <td style={tdStyle}>
                  <strong>
                    ${lineItems.reduce((sum,row)=>sum+row.qty*row.price,0).toFixed(2)}
                  </strong>
                </td>
                <td style={tdStyle}></td>
              </tr>
            </tbody>
          </table>
        </Box>
        <Box sx={{ mt:2 }}>
          <Button startIcon={<AddIcon />} size="small">
            Add row
          </Button>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box
        sx={{
          display:'flex',
          gap:2,
          pt:2,
          borderTop:'1px solid #ddd',
          justifyContent:'flex-end',
          bgcolor:'#fff',
          boxShadow:'0 -2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Button variant="contained" color="success">
          Create work order
        </Button>
        <Button variant="text">Add another</Button>
        <Button variant="text" onClick={() => navigate(-1)}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

// table cell styles
const thStyle = {
  textAlign: 'left',
  borderBottom: '1px solid #ddd',
  padding: '8px',
  background: '#fafafa'
};
const tdStyle = {
  borderBottom: '1px solid #eee',
  padding: '8px'
};